import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Megaphone } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Textarea } from '@/components/elements/textarea'
import { submitFeedback } from '@/services/feedback.service'
import { RootStoreContext } from '@/stores/RootStore'
import { cn, toast } from '@/utils/helpers'

const SENTRY_IS_ENABLED = import.meta.env.VITE_SENTRY_ENABLED

const FeedbackButton = observer(({ className }) => {
  const { course: courseContext } = useContext(RootStoreContext)
  const [open, setOpen] = useState(false)
  const { course, courseContent, quizQuestion } = courseContext

  const form = useForm({
    defaultValues: {
      message: '',
    },
  })

  const { mutate: submitFeedbackMutation, isPending } = useMutation({
    mutationFn: async (data) => {
      let eventId = null
      if (SENTRY_IS_ENABLED) {
        eventId = Sentry.captureFeedback({
          message: data.message,
          tags: { course, courseContent, quizQuestion },
        })
      }
      return submitFeedback({ data, eventId })
    },
    onSuccess: () => {
      toast('Thank you for your feedback!', 'success')
      form.reset()
      setOpen(false)
    },
    onError: (error) => {
      toast(error.message || 'Failed to submit feedback', 'error')
    },
  })

  const openFeedbackForm = () => {
    setOpen(true)
  }

  const handleSubmit = (data) => {
    submitFeedbackMutation(data)
  }

  return (
    <>
      <Button className={cn(className, 'rounded-full')} onClick={openFeedbackForm}>
        <Megaphone /> Feedback
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent setOpen={setOpen}>
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>
              Let us know if you spot any issues or have any questions.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="message"
                rules={{
                  required: 'Message is required',
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={5} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Sending...' : 'Send Feedback'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
})
export default FeedbackButton
